/**
 * ArchLang v0.12 — pure DSM (dependency-structure-matrix) partitioning.
 *
 * `partitionDsm(n, edges)` runs Tarjan SCC → condensation → topological layering
 * over a small projected label graph (nodes = the shown axis labels, edges = the
 * couplings between them). It yields a permutation that drops forward coupling
 * into the LOWER triangle and leaves feedback (cycle / back-edges) in the UPPER
 * triangle — the DSM's payload — plus the strongly-connected blocks (cycles) for
 * on-diagonal boxing. Self-contained: NOT the delta-engine closure SCC
 * (`closure.ts`), which is tuned for stable-id invalidation over the whole graph.
 *
 * ## Order convention
 * Rows are sources, columns are targets; cell `(i, j)` present means `i → j`
 * (row `i` depends on column `j`). Sinks come first: a target is ordered before
 * its source, so a DAG edge lands strictly below the diagonal. The residual
 * above-diagonal marks are exactly the intra-cycle back-edges.
 *
 * ## Determinism
 * SCCs are ordered by (layer ascending, then smallest member index); members
 * within an SCC by index. Callers pass labels pre-sorted, so index order = label
 * order — the whole partition is a pure function of `(n, edges)`.
 */

/** One strongly-connected block (a diagonal box when it is a cycle). */
export interface DsmBlock {
  /** Original label indices in this block — sorted. */
  readonly members: readonly number[];
  /** True when the block is a real cycle (≥2 members, or a self-loop). */
  readonly isCycle: boolean;
  /** Topological layer — 0 = sink (no outbound coupling), rising toward sources. */
  readonly layer: number;
}

/** The DSM partition: a permutation + its cycle blocks + the feedback edge set. */
export interface DsmPartition {
  /** A permutation of `[0..n)`: the original label index at each ordered position. */
  readonly order: readonly number[];
  /** SCC blocks, in output (ordered-position) order. */
  readonly blocks: readonly DsmBlock[];
  /** `${u}->${v}` (original indices) for every back-edge — the above-diagonal marks. */
  readonly feedback: ReadonlySet<string>;
}

/** An `u → v` coupling between label indices (self-loops allowed). */
export type DsmEdge = readonly [number, number];

/**
 * Partition an `n`-node coupling graph into a sink-first DSM order + cycle blocks
 * + feedback set. `edges` are `[from, to]` label-index pairs; duplicates and
 * self-loops are tolerated (a self-loop marks its singleton block a cycle).
 */
export function partitionDsm(n: number, edges: readonly DsmEdge[]): DsmPartition {
  // Deduped forward adjacency + the self-loop set (a self-loop is a 1-node cycle).
  const fwd: number[][] = Array.from({ length: n }, () => []);
  const seen = new Set<string>();
  const selfLoop = new Set<number>();
  for (const [u, v] of edges) {
    if (u === v) {
      selfLoop.add(u);
      continue;
    }
    const key = u + "," + v;
    if (seen.has(key)) continue;
    seen.add(key);
    fwd[u]!.push(v);
  }

  const comp = tarjanScc(n, fwd); // node → SCC id (dense, 0-based)
  const sccCount = n === 0 ? 0 : Math.max(...comp) + 1;

  // SCC members (sorted by original index for determinism).
  const members: number[][] = Array.from({ length: sccCount }, () => []);
  for (let node = 0; node < n; node++) members[comp[node]!]!.push(node);

  // Condensation edges (distinct SCC → SCC) + per-SCC longest-path layer.
  const condOut: Set<number>[] = Array.from({ length: sccCount }, () => new Set());
  for (let u = 0; u < n; u++) {
    for (const v of fwd[u]!) {
      if (comp[u]! !== comp[v]!) condOut[comp[u]!]!.add(comp[v]!);
    }
  }
  const layer = new Int32Array(sccCount).fill(-1);
  const layerOf = (s: number): number => {
    if (layer[s]! >= 0) return layer[s]!;
    let best = 0;
    for (const t of condOut[s]!) best = Math.max(best, 1 + layerOf(t));
    return (layer[s] = best);
  };
  for (let s = 0; s < sccCount; s++) layerOf(s);

  // Order SCCs: sinks first (layer asc), tie-break by smallest member index.
  const sccOrder = Array.from({ length: sccCount }, (_, s) => s).sort((a, b) => {
    if (layer[a]! !== layer[b]!) return layer[a]! - layer[b]!;
    return members[a]![0]! - members[b]![0]!;
  });

  const order: number[] = [];
  const blocks: DsmBlock[] = [];
  for (const s of sccOrder) {
    const mem = members[s]!;
    order.push(...mem);
    blocks.push({
      members: mem,
      isCycle: mem.length > 1 || selfLoop.has(mem[0]!),
      layer: layer[s]!,
    });
  }

  // Feedback = an edge whose target lands at or after its source in the order
  // (strictly above the diagonal). Guaranteed to be exactly the intra-cycle
  // back-edges given the sink-first order.
  const pos = new Int32Array(n);
  order.forEach((orig, p) => (pos[orig] = p));
  const feedback = new Set<string>();
  for (const [u, v] of edges) {
    if (u === v) continue;
    if (pos[v]! > pos[u]!) feedback.add(u + "->" + v);
  }

  return { order, blocks, feedback };
}

/**
 * Iterative Tarjan SCC over a forward adjacency list → `node → SCC id`. SCC ids
 * are assigned in reverse-topological discovery order; callers re-sort, so only
 * the partition (which nodes share an id) is contractual, not the id values.
 */
function tarjanScc(n: number, fwd: readonly number[][]): Int32Array {
  const index = new Int32Array(n).fill(-1);
  const low = new Int32Array(n);
  const onStack = new Uint8Array(n);
  const comp = new Int32Array(n).fill(-1);
  const stack: number[] = [];
  let counter = 0;
  let sccId = 0;

  // Explicit DFS frames: (node, next-neighbour-position).
  for (let root = 0; root < n; root++) {
    if (index[root]! >= 0) continue;
    const wNode: number[] = [root];
    const wPos: number[] = [0];
    while (wNode.length > 0) {
      const u = wNode[wNode.length - 1]!;
      if (wPos[wPos.length - 1] === 0 && index[u]! < 0) {
        index[u] = low[u] = counter++;
        stack.push(u);
        onStack[u] = 1;
      }
      const adj = fwd[u]!;
      let recursed = false;
      let p = wPos[wPos.length - 1]!;
      while (p < adj.length) {
        const v = adj[p]!;
        p++;
        if (index[v]! < 0) {
          wPos[wPos.length - 1] = p;
          wNode.push(v);
          wPos.push(0);
          recursed = true;
          break;
        } else if (onStack[v]) {
          low[u] = Math.min(low[u]!, index[v]!);
        }
      }
      if (recursed) continue;
      wPos[wPos.length - 1] = p;

      if (low[u] === index[u]) {
        // u roots an SCC — pop the component.
        let w = -1;
        do {
          w = stack.pop()!;
          onStack[w] = 0;
          comp[w] = sccId;
        } while (w !== u);
        sccId++;
      }
      // Pop this frame, fold low-link into the parent.
      wNode.pop();
      wPos.pop();
      if (wNode.length > 0) {
        const parent = wNode[wNode.length - 1]!;
        low[parent] = Math.min(low[parent]!, low[u]!);
      }
    }
  }
  return comp;
}
